'use client';
import CompareIcon from '@/assets/icons/icon-compares.svg';
import HeartIcon from '@/assets/icons/icon-heart.svg';
import MenuIcon from '@/assets/icons/icon-menu.svg';
import ShoppingCardIcon from '@/assets/icons/icon-shopping-cart.svg';
import { PasswordContent } from '@/components';
import DrawerContent from '../drawer-content';

import { SearchInput } from '@/components/ui/search-input';
import {
  useDraftOrderQuery,
  useUserDraftOrdersQuery,
  useUserFavoritesQuery,
} from '@/graphql';
import useHasMounted from '@/hooks/useHasMounted';
import { Link, useRouter } from '@/i18n/navigation';
import comparisonStore from '@/zustand/comparison';
import { favoritesStore } from '@/zustand/favourite';
import { useGuestStore } from '@/zustand/guestStore';
import useUserStore from '@/zustand/useUser';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import { Drawer, Modal } from 'rsuite';
import styles from './headerBottom.module.scss';
import SignInButton from './sign-in-button';
import useCatalogStore from '@/zustand/useOpenCatalog';

const HeaderBottom = () => {
  const [openModal, setOpenModal] = useState(false);
  const { open, setOpen } = useCatalogStore();
  const headerRef = useRef<HTMLDivElement>(null);
  const comparison = comparisonStore(e => e.comparison);
  const favorites = favoritesStore(e => e.favorites);
  const { width } = useWindowSize();
  const userData = useUserStore((user: any) => user?.user);
  const route = useRouter();
  const hasMounted = useHasMounted();
  const isLoggedIn = Boolean(userData?.id);
  const orderId = useGuestStore(state => state.guestOrderId);
  const guestFavoriteListArray = useGuestStore(state => state.favoriteList);

  const { data: draftOrdersData } = useUserDraftOrdersQuery({
    variables: {
      userId: userData?.id,
    },
  });
  const {
    data: favoriteData,
    loading,
    refetch: refetchFavoriteData,
  } = useUserFavoritesQuery({
    variables: { first: 10 },
    context: {
      requiresAuth: true,
    },
  });
  const { data: guestDraftOrdersData, refetch: refetchGuestDraftOrdersData } =
    useDraftOrderQuery({
      variables: {
        id: orderId || '',
      },
      // skip: isLoggedIn || !orderId, // Skip if user is logged in or there's no orderId
    });
  const handleClose = () => setOpenModal(false);
  const t = useTranslations('General');

  useEffect(() => {
    const handleScroll = () => {
      const header = headerRef.current;

      if (!header) return;

      header.classList.add(styles.fixed);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!hasMounted) return null;

  return (
    <>
      <div ref={headerRef} className={styles.headerBottomWrapper}>
        <div className={styles.headerBottom}>
          <div className={styles.headerBottom__wrapper}>
            <button
              type="button"
              className={styles.headerBottom__left}
              onClick={() => setOpen(true)}
            >
              <MenuIcon />
              <span className={styles.headerBottom__catalog}>
                {t('catalog-products')}
              </span>
            </button>

            <div className={styles.headerBottom__searchBar}>
              <SearchInput />
            </div>

            <div className={styles.headerBottom__right}>
              <SignInButton open={open} setOpenModal={setOpenModal} />

              <div
                className={`${styles.headerBottom__icon} ${styles.headerBottom__cart}`}
              >
                <Link
                  href={'/pages/comparison'}
                  className={styles.headerBottom__icon}
                >
                  <CompareIcon />
                  <p>{t('comparison')}</p>
                </Link>

                {comparison?.length === 0 ? null : (
                  <span className={styles.headerBottom__cartCount}>
                    {comparison?.length}
                  </span>
                )}
              </div>

              <div
                className={`${styles.headerBottom__icon} ${styles.headerBottom__cart}`}
              >
                <Link
                  href={'/pages/favorites'}
                  className={styles.headerBottom__icon}
                >
                  <HeartIcon />
                  <p>{t('favorites')}</p>
                </Link>
                {(isLoggedIn
                  ? (favoriteData?.me?.favorites?.edges?.length ?? 0) > 0
                  : guestFavoriteListArray?.length > 0) && (
                  <span className={styles.headerBottom__cartCount}>
                    {isLoggedIn
                      ? favoriteData?.me?.favorites?.edges?.length
                      : guestFavoriteListArray?.length}
                  </span>
                )}
              </div>
              <div
                onClick={() => route.push('/cart')}
                className={`${styles.headerBottom__icon} ${styles.headerBottom__cart}`}
              >
                <ShoppingCardIcon />
                <div>{t('cart')}</div>
                {(
                  isLoggedIn
                    ? draftOrdersData?.userDraftOrders?.[0] &&
                      draftOrdersData?.userDraftOrders?.[0]?.lines?.length
                    : guestDraftOrdersData?.draftOrder?.lines?.length
                ) ? (
                  <span className={styles.headerBottom__cartCount}>
                    {isLoggedIn
                      ? draftOrdersData?.userDraftOrders?.[0]?.lines?.length
                      : guestDraftOrdersData?.draftOrder?.lines?.length}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Drawer
        size={`${
          width <= 767
            ? 'calc(100% - 0px'
            : width <= 1241
              ? 'calc(100% - 50px'
              : width <= 1510
                ? 'calc(100% - 100px'
                : width <= 1750
                  ? 'calc(100% - 258px'
                  : 'calc(100% - 490px'
        })`}
        placement="left"
        onClose={() => setOpen(false)}
        open={open}
        closeButton={false}
      >
        <Drawer.Body style={{ padding: '0' }}>
          <div style={{ height: '100%' }}>
            <DrawerContent closeButton={() => setOpen(false)} />
          </div>
        </Drawer.Body>
      </Drawer>
      <Modal
        size={'xs'}
        open={openModal}
        onClose={handleClose}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <PasswordContent onClose={handleClose} />
      </Modal>
    </>
  );
};

export default HeaderBottom;
